import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import ArticleCard, { categoryName } from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import categories from '@/mocks/categories';
import type { FormEvent } from 'react';

const allowed = ['love-notes', 'heartbeat', 'official', 'couple-things', 'all'];

export default function Fresh() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || 'all';
  const qParam = searchParams.get('q') || '';
  const active = allowed.includes(categoryParam) ? categoryParam : 'all';

  const [keyword, setKeyword] = useState(qParam);

  const list = useMemo(() => {
    let base = active === 'all' ? articles : articles.filter((a) => a.category === active);
    const kw = qParam.trim();
    if (kw) {
      base = base.filter(
        (a) => a.title.includes(kw) || a.excerpt.includes(kw) || a.tags.some((t) => t.includes(kw)),
      );
    }
    return base;
  }, [active, qParam]);

  const hot = [...articles].sort((a, b) => b.heat - a.heat).slice(0, 5);

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === 'all') params.delete('category');
      else params.set('category', next.category);
    }
    if (next.q !== undefined) {
      if (next.q) params.set('q', next.q);
      else params.delete('q');
    }
    setSearchParams(params, { replace: true });
  };

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateParams({ q: keyword.trim() });
  };

  const currentName = active === 'all' ? '全部甜瓜' : categoryName(active);
  const currentDesc =
    active === 'all'
      ? '按时间顺序排好的全部甜味瓜料，从最新的一条开始往下翻。'
      : categories.find((c) => c.slug === active)?.desc || '';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentName} - 瓜田速递`,
    url: 'https://lhyf56.cn/fresh',
    description: '糖心吃瓜瓜田速递，聚合恋爱小料、心动瞬间、官宣现场与情侣小事。',
    isPartOf: { '@type': 'WebSite', name: '糖心吃瓜', url: 'https://lhyf56.cn/' },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: list.slice(0, 10).map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: a.title,
        url: `https://lhyf56.cn/article/${a.slug}`,
      })),
    },
  };

  return (
    <>
      <title>{`${currentName} - 瓜田速递 | 糖心吃瓜`}</title>
      <meta name="description" content={`糖心吃瓜瓜田速递：${currentDesc} 分类浏览恋爱小料、心动瞬间、官宣现场与情侣小事。`} />
      <meta name="keywords" content="糖心吃瓜,瓜田速递,恋爱小料,心动瞬间,官宣,情侣小事,甜味瓜" />
      <link rel="canonical" href="https://lhyf56.cn/fresh" />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content={`${currentName} - 瓜田速递 | 糖心吃瓜`} />
      <meta property="og:description" content="按分类浏览糖心吃瓜的全部甜味瓜料。" />
      <meta property="og:url" content="https://lhyf56.cn/fresh" />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <SiteHeader />
      <main className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-800">瓜田速递</span>
          {active !== 'all' && (
            <>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-800">{currentName}</span>
            </>
          )}
        </nav>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3 lg:order-1">
            <div className="lg:sticky lg:top-24 flex flex-col gap-6">
              <div className="rounded-xl border border-background-200 bg-background-100 p-4">
                <h2 className="text-sm font-medium text-foreground-900">甜瓜分类</h2>
                <div className="mt-3 flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => updateParams({ category: 'all' })}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors cursor-pointer ${
                      active === 'all' ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    全部甜瓜
                    <span className="text-xs">{articles.length}</span>
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => updateParams({ category: c.slug })}
                      className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors cursor-pointer ${
                        active === c.slug ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:bg-background-200'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <i className={c.icon} aria-hidden="true"></i>
                        {c.name}
                      </span>
                      <span className="text-xs">{c.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-background-200 bg-secondary-50 p-4">
                <h2 className="flex items-center gap-1.5 text-sm font-medium text-foreground-900">
                  <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                  甜度热榜
                </h2>
                <ol className="mt-3 flex flex-col gap-3">
                  {hot.map((a, i) => (
                    <li key={a.id} className="flex gap-3">
                      <span
                        className={`shrink-0 font-heading text-lg w-5 text-center ${
                          i < 3 ? 'text-primary-500' : 'text-foreground-400'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <Link
                        to={`/article/${a.slug}`}
                        className="text-sm text-foreground-700 leading-snug line-clamp-2 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        {a.title}
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9 lg:order-2">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-xs tracking-widest text-primary-600">FRESH FEED</p>
                <h1 className="mt-2 font-heading text-2xl md:text-4xl text-foreground-950">{currentName}</h1>
                <p className="mt-2 max-w-xl text-sm text-foreground-600">{currentDesc}</p>
              </div>
              <form onSubmit={handleSearch} className="flex w-full md:w-72 gap-2">
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索瓜料关键词"
                  aria-label="搜索瓜料"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-300"
                />
                <button
                  type="submit"
                  aria-label="开始搜索"
                  className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-search-line" aria-hidden="true"></i>
                </button>
              </form>
            </div>

            {qParam && (
              <p className="mt-5 text-sm text-foreground-600">
                关键词「<span className="text-primary-600">{qParam}</span>」共找到 {list.length} 条瓜料
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('');
                    updateParams({ q: '' });
                  }}
                  className="ml-3 text-xs text-foreground-500 underline hover:text-primary-600 cursor-pointer"
                >
                  清除
                </button>
              </p>
            )}

            {list.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {list.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            ) : (
              <div className="mt-10 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-emotion-sad-line text-2xl" aria-hidden="true"></i>
                </span>
                <p className="mt-4 text-sm text-foreground-700">这片瓜田暂时没有符合条件的甜瓜</p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('');
                    setSearchParams(new URLSearchParams(), { replace: true });
                  }}
                  className="mt-4 rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  回到全部甜瓜
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}